import React, {
  useContext, useEffect, useRef, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import Box from '@nubank/nuds-web/components/Box/Box';
import Grid from '@nubank/nuds-web/components/DeprecatedGrid/DeprecatedGrid';
import { NuDsContext } from '@nubank/nuds-web/components/NuDSProvider/NuDSProvider';
import Typography from '@nubank/nuds-web/components/Typography/Typography';
import { isType } from '@nubank/nuds-web/utils/isType/isType';
import { useBreakpointsMediaDown } from '@nubank/nuds-web/styles/breakpoints';

import useInView from '@nubank/www-latam-commons/utils/hooks/useInView';

import { StyledTypographyTitle, StyledTypographyText } from './styles/Typography';
import { StyledVideo } from './styles/Video';
import { ArrowSVG } from './styles/Arrow';

function SectionOverview({ index }) {
  const { formatMessage } = useContext(NuDsContext);
  const overviewTopics = formatMessage({ id: `cms.pixPage.pixCardsArea[${index}].overview` });
  const isMobile = useBreakpointsMediaDown('md');

  const videoRef = useRef();

  const [inViewRef, isVisible] = useInView({
    intersectionOptions: {
      triggerOnce: true,
      rootMargin: '70px',
    },
  });

  const setRefs = useCallback(node => {
    videoRef.current = node;
    inViewRef(node);
  }, [inViewRef]);

  useEffect(() => {
    if (isVisible) {
      const $video = videoRef.current;
      $video.querySelectorAll('source')
        .forEach($source => ($source.setAttribute('src', $source.dataset.src)));
      $video.load();
    }
  }, [isVisible]);

  return (
    <Box
      tag="section"
      paddingHorizontal={{ xs: '0x', md: '8x', lg: '12x' }}
      paddingVertical={{ xs: '16x', md: '20x', lg: '32x' }}
    >
      <div id="saiba-mais">
        <Grid row spacing={{ xs: '0x', md: '4x' }}>
          <Grid col={{ xs: 12, lg: 5, xl: 4 }} offset={{ lg: 1 }}>
            <Box
              marginBottom={{ xs: '16x', md: '16x', lg: '0x' }}
              paddingHorizontal={{ xs: '6x', md: '0x' }}
            >
              <StyledTypographyTitle
                variant="title"
                color="black"
                intlKey={`cms.pixPage.pixCardsArea[${index}].title`}
              />
              <StyledTypographyText
                variant="subtitle"
                tag="p"
                color="black"
                intlKey={`cms.pixPage.pixCardsArea[${index}].description`}
              />
            </Box>
          </Grid>
          <Grid col={{ xs: 12, lg: 5, xl: 6 }}>
            <Box
              display={{ lg: 'flex' }}
              height={{ lg: '100%' }}
              alignItems={{ lg: 'center' }}
            >
              {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
              {isType('boolean', isMobile) && (
                <StyledVideo ref={setRefs} controls>
                  <source
                    type="video/webm"
                    data-src={isMobile ? '/videos/pix/pix-advertising@mobile320.webm' : '/videos/pix/pix-advertising@tablet768.webm'}
                  />
                  <source
                    type="video/mp4"
                    data-src={isMobile ? '/videos/pix/pix-advertising@mobile320.mp4' : '/videos/pix/pix-advertising@tablet768.mp4'}
                  />
                  <track label="Português" kind="captions" srcLang="pt-BR" src="/videos/pix/pix-advertising.vtt" default />
                </StyledVideo>
              )}
            </Box>
          </Grid>
          <Grid col={{ xs: 12, md: 2 }} offset={{ md: 2, lg: 1 }}>
            <Box
              marginHorizontal={{ xs: '6x', md: 0 }}
              marginTop={{ xs: '16x', lg: '28x' }}
              width="62px"
            >
              <ArrowSVG />
            </Box>
          </Grid>
          <Grid col={{ xs: 12, md: 8, lg: 10 }} offset={{ lg: 1 }}>
            <Box
              marginTop={{ xs: '8x', md: '16x', lg: '12x' }}
              paddingHorizontal={{ xs: '6x', md: 0 }}
            >
              <Grid row tag="ul" spacing={0}>
                {
                  overviewTopics.map(({ id, title, description }, topicIndex) => (
                    <Grid key={id} tag="li" col={{ xs: 12, lg: 6 }}>
                      <Box
                        marginTop={{ xs: topicIndex ? '8x' : '0x', lg: topicIndex < 2 ? '0x' : '8x' }}
                        paddingRight={{ lg: topicIndex % 2 ? '0x' : '4x' }}
                        paddingLeft={{ lg: topicIndex % 2 ? '4x' : '0x' }}
                      >
                        <Typography
                          variant="subtitle"
                          color="black"
                          gutterBottom={0.5}
                        >
                          {title}
                        </Typography>
                        <Typography>
                          {description}
                        </Typography>
                      </Box>
                    </Grid>
                  ))
                }
              </Grid>
            </Box>
          </Grid>
        </Grid>
      </div>
    </Box>
  );
}

SectionOverview.propTypes = {
  index: PropTypes.number.isRequired,
};

export default React.memo(SectionOverview);
